import { createServerFn } from "@tanstack/react-start";
import { desc, eq, gte, sql } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requireAuth } from "../auth/session.server";

function todayUtcDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoUtc(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function fillDailySeries(
  rows: { date: string; uniqueVisitors: number; totalPageViews: number }[],
  days: number,
): { date: string; uniqueVisitors: number; totalPageViews: number }[] {
  const byDate = new Map(rows.map((r) => [r.date, r]));
  const series: { date: string; uniqueVisitors: number; totalPageViews: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = daysAgoUtc(i);
    const row = byDate.get(date);
    series.push({
      date,
      uniqueVisitors: row?.uniqueVisitors ?? 0,
      totalPageViews: row?.totalPageViews ?? 0,
    });
  }
  return series;
}

export const getDashboardAnalytics = createServerFn({ method: "GET" }).handler(async () => {
  await requireAuth();
  const db = getDb();
  const since = daysAgoUtc(29);

  const dailyRows = await db
    .select({
      date: schema.analyticsDailyStats.date,
      uniqueVisitors: schema.analyticsDailyStats.uniqueVisitors,
      totalPageViews: schema.analyticsDailyStats.totalPageViews,
    })
    .from(schema.analyticsDailyStats)
    .where(gte(schema.analyticsDailyStats.date, since))
    .orderBy(schema.analyticsDailyStats.date);

  const dailySeries = fillDailySeries(
    dailyRows.map((r) => ({
      date: r.date,
      uniqueVisitors: r.uniqueVisitors,
      totalPageViews: r.totalPageViews,
    })),
    30,
  );

  const today = todayUtcDate();
  const todayRow = dailySeries.find((d) => d.date === today);

  const sinceDate = new Date();
  sinceDate.setUTCDate(sinceDate.getUTCDate() - 30);

  const mapRows = await db
    .select({
      city: schema.siteVisits.city,
      region: schema.siteVisits.region,
      countryName: schema.siteVisits.countryName,
      countryCode: schema.siteVisits.countryCode,
      latitude: schema.siteVisits.latitude,
      longitude: schema.siteVisits.longitude,
      count: sql<number>`count(*)::int`,
    })
    .from(schema.siteVisits)
    .where(gte(schema.siteVisits.createdAt, sinceDate))
    .groupBy(
      schema.siteVisits.city,
      schema.siteVisits.region,
      schema.siteVisits.countryName,
      schema.siteVisits.countryCode,
      schema.siteVisits.latitude,
      schema.siteVisits.longitude,
    );

  const mapLocations = mapRows
    .filter((r) => r.latitude != null && r.longitude != null)
    .map((r) => ({
      city: r.city,
      region: r.region,
      countryName: r.countryName,
      countryCode: r.countryCode,
      latitude: r.latitude as number,
      longitude: r.longitude as number,
      count: r.count,
    }));

  return {
    todayUniqueVisitors: todayRow?.uniqueVisitors ?? 0,
    dailySeries,
    mapLocations,
  };
});

export const getVisitorLog = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        limit: z.number().int().min(1).max(200).optional(),
        offset: z.number().int().min(0).optional(),
      })
      .optional(),
  )
  .handler(async ({ data }) => {
    await requireAuth();
    const limit = data?.limit ?? 50;
    const offset = data?.offset ?? 0;
    const db = getDb();

    const rows = await db
      .select({
        id: schema.siteVisits.id,
        path: schema.siteVisits.path,
        ipMasked: schema.siteVisits.ipMasked,
        city: schema.siteVisits.city,
        region: schema.siteVisits.region,
        countryName: schema.siteVisits.countryName,
        countryCode: schema.siteVisits.countryCode,
        createdAt: schema.siteVisits.createdAt,
      })
      .from(schema.siteVisits)
      .orderBy(desc(schema.siteVisits.createdAt))
      .limit(limit)
      .offset(offset);

    const [{ total }] = await db.select({ total: sql<number>`count(*)::int` }).from(schema.siteVisits);

    return {
      rows: rows.map((r) => ({
        id: r.id,
        path: r.path,
        ipMasked: r.ipMasked,
        location: [r.city, r.region, r.countryName ?? r.countryCode].filter(Boolean).join(", ") || "Unknown",
        createdAt: r.createdAt.toISOString(),
      })),
      total,
    };
  });

export const getInsightImpressions = createServerFn({ method: "GET" })
  .inputValidator(z.object({ insightId: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requireAuth();
    const db = getDb();
    const [row] = await db
      .select({ impressions: schema.insightImpressions.impressions })
      .from(schema.insightImpressions)
      .where(eq(schema.insightImpressions.insightId, data.insightId))
      .limit(1);
    return { impressions: row?.impressions ?? 0 };
  });
